<template>
  <v-container fluid>
  <v-card class="pa-4" elevation="2">
    <v-row class="align-center">
      <v-col class="headline mb-4">
         <h2>Planimetria</h2>
      </v-col>
    </v-row>
      
    <v-row class="mb-3" dense>
        <v-col cols="12" sm="6" md="4">
          <v-menu
            v-model="menuDate"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template #activator="{ props }">
              <v-text-field
                v-bind="props"
                :model-value="formatHuman(selectedDate)"
                label="Fecha"
                readonly
                density="comfortable"
                append-inner-icon="mdi-calendar"
              />
            </template>

            <v-date-picker
              v-model="selectedDate"
              show-adjacent-months
              @update:modelValue="menuDate = false"
            />
          </v-menu>
        </v-col>

        <v-col cols="12" sm="6" md="4">
          <v-select
            @update:model-value="onCanviaDelegacio"
            :items="delegacions"
            item-title="label"
            item-value="id"
            label="Almacén"
            density="comfortable"
            hint="Selecciona un almacén (opcional)"
          />
        </v-col>

        <v-col cols="12" sm="6" md="4"> 
            <v-text-field
                v-model="search"
                label="Buscar..."
                prepend-inner-icon="mdi-magnify"
                density="comfortable"
                hide-details
                clearable
                style="min-width: 260px"
              />
         </v-col>
      </v-row>


      <v-overlay :model-value="isLoading" class="align-center justify-center" persistent>
        <v-progress-circular indeterminate size="48" />
      </v-overlay>


        <v-alert
        v-if="missatge"
        :type="estat ? 'success' : 'error'"
        class="mb-4"
        variant="tonal"
      >
        {{ missatge }}
      </v-alert>

      <v-skeleton-loader
        v-if="carregant"
        type="table"
        class="mb-4"
      />

      <v-data-table
        v-else
        :headers="headers"
        :items="files"
        :search="search"
        :group-by="[{ key: 'equip', order: 'asc' }]"
        item-key="__rowid"
        show-group-by
        items-per-page="10"
        class="elevation-1"
      >
        <template #item.unitats="{ item }">
          <v-chip density="comfortable">
            {{ item.unitats?.toLocaleString?.() ?? item.unitats }}
          </v-chip>
        </template>

        <template #item.data_distribucio="{ item }">
          <span>{{ item.data_distribucio }}</span>
        </template>

        <template #group-header="{ item, columns, toggleGroup, isGroupOpen }">
          <tr @click="toggleGroup(item)" style="cursor: pointer">
            <td :colspan="columns.length" class="font-weight-medium">
              <v-icon size="small" class="mr-2">
                {{ isGroupOpen(item) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
              </v-icon>
              Equipo: <strong>{{ item.value }}</strong>
              <span class="ml-4 text-medium-emphasis">
                ({{ item.items.length }} líneas)
              </span>
              <span
                v-if="item.items?.[0]?.raw?.equip_email"
                class="ml-6 text-medium-emphasis"
              >
                · {{ item.items[0].raw.equip_email }}
              </span>
            </td>
          </tr>
        </template>

        <template #no-data>
          <v-empty-state
            headline="Sin resultados"
            title="No hay lineas para mostrar"
            text="Canvia la busqueda o recarga"
          />
        </template>
      </v-data-table>

      <!-- Botó d'enviament -->
      <v-btn
        color="primary"
        :loading="enviant"
        :disabled="carregant || files.length === 0"
        class="ctrl"
        @click="confirmOpen = true"
      >
        Enviar partes de trabajo a todos los equipos
      </v-btn>


      <!-- Prova d'enviament de correu -->
      <v-divider class="my-4" />

      <v-card variant="tonal" class="pa-4 mb-4">
        <v-card-title class="text-subtitle-1 px-0">
          Probar envío de correo
        </v-card-title>

        <v-card-text class="px-0">
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="emailProva"
                label="Email de prueba"
                type="email"
                density="comfortable"
                prepend-inner-icon="mdi-email"
                :disabled="enviantProva"
                clearable
              />
            </v-col>

            <v-col cols="12" md="3" class="d-flex align-center">
              <v-btn
                color="primary"
                :loading="enviantProva"
                :disabled="!emailProva"
                @click="enviarProva"
              >
                Enviar prueba
              </v-btn>
            </v-col>
          </v-row>

          <div class="text-caption text-medium-emphasis">
            Envía un correo simple para comprobar que la configuración funciona.
          </div>
        </v-card-text>
      </v-card>


      <!-- Diàleg de confirmació -->
      <v-dialog v-model="confirmOpen" max-width="520">
        <v-card>
          <v-card-title class="text-h6">Confirmar envío</v-card-title>
          <v-card-text>
            Se enviará una nota a <strong>{{ equipsUniques.length }}</strong> equipos
            para la fecha <strong>{{ dataEU.value }}</strong> ({{ nomAlmacen }}).
            ¿Deseas continuar?
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn variant="text" @click="confirmOpen = false">Cancelar</v-btn>
            <v-btn color="primary" :loading="enviant" @click="enviarParts">
              Enviar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

  </v-card>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axios from 'axios'

const carregant = ref(false)
const estat = ref(false)
const missatge = ref('')
const fase = ref('')
const search = ref('')
const files = ref([])
const isLoading = ref(false)

const confirmOpen = ref(false)
const enviant = ref(false)

const nomAlmacen = computed(() => (delegacioId.value === 453 ? 'Barcelona' : 'Madrid'))

const emailProva = ref('')
const enviantProva = ref(false)

const headers = [
  { title: 'Campaña', key: 'campanya', sortable: true },
  { title: 'Descripción', key: 'descripcio', sortable: true },
  { title: 'Cliente', key: 'client', sortable: true },
  { title: 'Ref folleto', key: 'ref_fullet', sortable: true },
  { title: 'Población', key: 'poblacio', sortable: true },
  { title: 'ZT', key: 'zt', sortable: true },
  { title: 'Unidades', key: 'unitats', sortable: true },
  { title: 'Tienda', key: 'botiga', sortable: true },
  { title: 'Notas', key: 'notes', sortable: false },
]

const delegacions = [
  { label: 'Barcelona', id: 453 },
  { label: 'Madrid', id: 502 },
]

// Token (mantén-lo si et cal)
const token = localStorage.getItem('token') || ''

// ---------- DATA REACTIVA ----------
const menuDate = ref(false)
const getTomorrow = () => {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() + 1)
  return d
}
const selectedDate = ref(getTomorrow())

const pad = (n) => String(n).padStart(2, '0')
const dataISO = computed(() => {
  const d = selectedDate.value
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
})
const dataEU = computed(() => {
  const d = selectedDate.value
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`
})
const formatHuman = (d) => {
  if (!d) return ''
  return d.toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' })
}

// Delegació (sense localStorage)
const delegacioId = ref(453)

// ---------- NORMALITZACIÓ ----------
function normalitzaResposta(resData) {
  console.log(resData)
  const sortida = []
const pushRow = (equipNom, equipEmail, equipId, row) => {
  const mapped = {
    campanya: row.Campanya ?? row.campaign ?? row.campaign_id ?? '',
    descripcio: row.Descripcio ?? row.description ?? '',
    client: row.Client ?? row.partner ?? '',
    ref_fullet: row.Ref_fullet ?? row.flyer_reference ?? '',
    poblacio: row.Poblacio ?? row.zone_city ?? '',
    provincia: row.Provincia ?? row.zone_state ?? row.zone_state_id ?? '',
    unitats: Number(row.Unitats ?? row.units ?? row.quantity ?? 0),
    data_distribucio: row.Data_distribucio ?? row.distribute_date ?? '',
    zt: row.zt ?? row.ZT ?? '',
    botiga: row.botiga ?? row.Botiga ?? '',
    notes: row.notes ?? row.Notes ?? '',

    equip: equipNom || '—',
    equip_email: equipEmail || '',
    equip_id: equipId || row.equip_id || row.equipId || row.Equipo_id || row.id_equip || '',
  }

  mapped.__rowid = `${mapped.equip}::${mapped.equip_email}::${mapped.equip_id}::${mapped.campanya}::${mapped.ref_fullet}::${mapped.poblacio}::${mapped.data_distribucio}::${Math.random().toString(36).slice(2)}`
  sortida.push(mapped)
}

  if (Array.isArray(resData)) {
    // ✅ FORMAT C: [{equipNom, equipEmail, linies:[...]}, ...]
    if (resData.length && Object.prototype.hasOwnProperty.call(resData[0] || {}, 'linies')) {
      resData.forEach(team => {
        const equipNom = team.equipNom || team.equip || '—'
        const equipEmail = team.equipEmail || ''
        const equipId = team.equipId || team.equip_id || team.id || team.id_equip || ''
        const linies = Array.isArray(team.linies) ? team.linies : []
        linies.forEach(l => pushRow(equipNom, equipEmail, equipId, l))
      })
    } else {
      // Antic FORMAT B: array pla d’ordres
      resData.forEach(r => pushRow(r.equip ?? '—', r.equipEmail ?? '', r))
    }
  } else if (resData && typeof resData === 'object') {
    // Antic FORMAT A: { "NomEquip": [ ... ] }
    Object.entries(resData).forEach(([equip, arr]) => {
      if (Array.isArray(arr)) arr.forEach(row => pushRow(equip, '', row))
    })
  }

  return sortida
}


// ---------- API ----------
const consultar = async () => {

  carregant.value = true
  fase.value = 'consultant'
  missatge.value = ''
  estat.value = false

  isLoading.value = true

  try {
    const res = await axios.get('/api/admin/planimetria/planificacio', {
      params: { data: dataISO.value, delegacio_id: delegacioId.value },
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })

    const items = normalitzaResposta(res.data)
    files.value = items

    estat.value = true
    const nomAlmacen = delegacioId.value === 453 ? 'Barcelona' : 'Madrid'
    missatge.value = `${items.length} líneas recibidas para ${dataEU.value} (almacén ${nomAlmacen})`
  } catch (err) {
    console.error(err)
    estat.value = false
    const apiMsg = err?.response?.data?.error || err?.message || 'Error desconocido'
    missatge.value = `Error consultando planimetría: ${apiMsg}`
  } finally {
    fase.value = ''
    carregant.value = false
  }

  isLoading.value = false
}

function onCanviaDelegacio(val) {
  delegacioId.value = val
  consultar()
}

// Deduplika equips per email (ignora buits)
const equipsUniques = computed(() => {
  const map = new Map()

  for (const r of files.value) {
    const email = (r.equip_email || '').trim()
    const name = (r.equip || '').trim()
    const id = r.equip_id || ''

    if (!email) continue
    if (!id) continue

    const key = `${email}::${id}`

    if (!map.has(key)) {
      map.set(key, {
        name: name || email,
        email,
        id,
      })
    }
  }

  return Array.from(map.values())
})

async function enviarParts () {
  enviant.value = true
  missatge.value = ''
  estat.value = false

  try {
    console.log('Equips a enviar:', equipsUniques.value)

    const res = await axios.post('/api/admin/planimetria/enviar-parts', {
      data: dataISO.value,
      equips: equipsUniques.value,
      test_email: 'xavi@traycco.es',
    }, {
      timeout: 30000,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })

    estat.value = true
    missatge.value = res.data?.message || 'Correos de prueba enviados correctamente'
    confirmOpen.value = false
  } catch (err) {
    console.error(err)

    estat.value = false

    const apiMsg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      'Error desconocido'

    missatge.value = `Error enviando correos: ${apiMsg}`
  } finally {
    enviant.value = false
  }
}

async function enviarProva () {
  enviantProva.value = true
  missatge.value = ''
  estat.value = false

  try {
    const res = await axios.post('/api/admin/planimetria/enviar-prova', {
      email: emailProva.value,
    }, {
      timeout: 30000,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })

    estat.value = true
    missatge.value = res.data?.message || 'Correo de prueba enviado correctamente'
  } catch (err) {
    console.error(err)

    estat.value = false

    const apiMsg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      'Error desconocido'

    missatge.value = `Error enviando correo de prueba: ${apiMsg}`
  } finally {
    enviantProva.value = false
  }
}


// Recarrega quan canvia la data (ara sí: és un ref)
watch(selectedDate, () => consultar())

onMounted(consultar)
</script>

